/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { U2000LUStateEnumNextAction } from "../u2000Core/reducer/formReducer/U2000LUState";
import U3139MinutesAddDlg from "./components/common/U3139MinutesAddDlg";
import { NS } from "./U3139";
import { luHandleAppiError, type U3139InitCreateMinute } from "./U3139Service";
import { useU3139InitCreateHandlers } from "./U3139UseApiHandlers";
import { useU3139Context } from "./U3139Context";

export type U3139FormNavigationState = {
  initPreview?: U3139InitCreateMinute;
};

interface U3139InitCreateMinuteDialogContextValue {
  openAddDialog: () => void;
}

const U3139InitCreateMinuteDialogContext = createContext<U3139InitCreateMinuteDialogContextValue | undefined>(undefined);

export const U3139InitCreateMinuteDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const { luStateHandlers } = useU3139Context();

  const handleInitCreateError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
    [t, toast],
  );

  const { initCreateMinute } = useU3139InitCreateHandlers({ onError: handleInitCreateError });

  const openAddDialog = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleConfirm = useCallback(
    async (projetNumber: string) => {
      const preview = await initCreateMinute({ projetNumber: projetNumber.trim() });
      if (!preview?.project?.id) {
        if (preview !== null) {
          toast.error(t("errInitCreateInvalidResponse", { ns: NS }), t("errorTitle", { ns: NS }));
        }
        return;
      }
      setOpen(false);
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom("/U3139");
      navigate("/U3139/new", { state: { initPreview: preview } satisfies U3139FormNavigationState });
    },
    [initCreateMinute, luStateHandlers, navigate, t, toast],
  );

  const value = useMemo(() => ({ openAddDialog }), [openAddDialog]);

  return (
    <U3139InitCreateMinuteDialogContext.Provider value={value}>
      {children}
      <U3139MinutesAddDlg isOpen={open} onClose={handleClose} onConfirm={handleConfirm} />
    </U3139InitCreateMinuteDialogContext.Provider>
  );
};

export function useU3139InitCreateMinuteDialog(): U3139InitCreateMinuteDialogContextValue {
  const ctx = useContext(U3139InitCreateMinuteDialogContext);
  if (!ctx) {
    throw new Error("useU3139InitCreateMinuteDialog must be used within U3139InitCreateMinuteDialogProvider");
  }
  return ctx;
}
